import { useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { alertError, alertSuccess } from "../../../../components/alert/swal";
import {
  setLoading,
  unsetLoading,
} from "../../../../store/loading/loading.slice";
import ContainerLayout from "../../../../components/container-layout/ContainerLayout";
import StateComponents from "../../../../components/state-component/StateComponents";
import { debouncedApiCall } from "../../../../utils/app.utils";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { routeName } from "../../../../routes/routeName";
import ContainerButton from "../../../../components/button/ContainerButton";
import AccordionUpload from "../../../../components/accordion/AccordionUpload";
import ImageTracking from "../../../../components/upload/MultiUploadFile";
import { colors } from "../../../../constants/colors";
import { edpexFormMasterAPI } from "../../../../api/edpex-form-master/edpex-form-master.api";
import { RootState } from "../../../../store/store";

interface UploadedFile {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  file: File;
}

const EdpexSampleProjectData = () => {
  const baseURL = useSelector((state:RootState) => state.api.image_api)
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [name, setName] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [multiUpload, setMultiImage] = useState<any[]>([]);
  const [mode, setMode] = useState<string>("VIEW");

  const handleFileUpload = async (
    files: File[],
    type: string
  ): Promise<number[]> => {
    if (files.length === 0) return [];

    try {
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append("files", file);
      });

      const res = await edpexFormMasterAPI.uploadFileEdpexFormMaster(
        type,
        formData
      );

      // Extract file IDs from the correct path: res.data.data
      const fileIds =
        res.data?.data?.map((file: any) => file.id_edpex_form_file) || [];
      return fileIds;
    } catch (error: any) {
      console.error("File upload error:", error);
      alertError(
        t("เกิดข้อผิดพลาด"),
        error?.response?.data?.message || t("อัปโหลดไฟล์ไม่สำเร็จ")
      );
      return [];
    }
  };

  // Helper to get file IDs (both existing and new uploads)
  const processUploadedFiles = async (
    files: UploadedFile[],
    type: string
  ): Promise<number[]> => {
    const newFileIds = await handleFileUpload(
      files.map((file) => file.file).filter(Boolean),
      type
    );

    const existingFileIds = files.map((file) => file.id).filter(Boolean);

    return [...existingFileIds, ...newFileIds];
  };

  // Main submit handler
  const handleSubmit = async (submitStatus: "active" | "เผยแพร่") => {
    if (!name.trim()) {
      alertError(t("เกิดข้อผิดพลาด"), t("กรุณากรอกชื่อ"));
      return;
    }

    try {
      dispatch(setLoading());

      // Process each file group
      const allImageIds = await processUploadedFiles(multiUpload, "FILE_IMAGE");
      const allDocumentIds = await processUploadedFiles(
        uploadedFiles,
        "FILE_DOCUMENT"
      );

      // Combine all file IDs
      const body: any = {
        form_type: "EXAMPLE_PROJECT",
        edpex_form_name: name,
        status: submitStatus,
        id_files: [...allImageIds, ...allDocumentIds],
      };

      const res = await edpexFormMasterAPI.updateEdpexFormMaster(body);

      if (res.data.status < 300) {
        const successMessage =
          submitStatus === "active"
            ? t("บันทึกข้อมูลสำเร็จ")
            : t("เผยแพร่ข้อมูลสำเร็จ");

        alertSuccess(t("สำเร็จ"), successMessage);
        setMode("VIEW");
        await handleGetData();
      }
    } catch (error: any) {
      const errorMessage =
        submitStatus === "active"
          ? t("บันทึกข้อมูลไม่สำเร็จ")
          : t("เผยแพร่ข้อมูลไม่สำเร็จ");

      alertError(
        t("เกิดข้อผิดพลาด"),
        error?.response?.data?.message || errorMessage
      );
    } finally {
      dispatch(unsetLoading());
    }
  };

  const handleGetData = useCallback(async () => {
    try {
      dispatch(setLoading());

      const res = await edpexFormMasterAPI.getEdpexFormMasterByType({
        form_type: "EXAMPLE_PROJECT",
        is_release: 0,
      });

      if (res.data?.status < 300 && res.data?.data) {
        const {
          edpex_form_name,
          document_files = [],
          image_files = [],
        } = res.data.data;

        setName(edpex_form_name || "");

        const mapFileData = (
          files: any[],
          type: "FILE_IMAGE" | "FILE_DOCUMENT"
        ): UploadedFile[] =>
          files.map((file) => ({
            id: file.id_edpex_form_file,
            name: file.file_name,
            size: file.file_size
              ? `${(file.file_size / 1024).toFixed(2)} KB`
              : "Unknown",
            uploadDate: file.modified_date || file.created_date || "N/A",
            file_path: file.file_path,
            fullUrl: `${baseURL}${file.file_path}${encodeURIComponent(
              file.file_name
            )}`,
            type,
            file: null as unknown as File, // Placeholder for UI compatibility
          }));

        setUploadedFiles([...mapFileData(document_files, "FILE_DOCUMENT")]);
        setMultiImage([...mapFileData(image_files, "FILE_IMAGE")]);
      }
    } catch (error: any) {
      console.error("Get data error:", error);
      alertError(t("เกิดข้อผิดพลาด"), t("โหลดข้อมูลไม่สำเร็จ"));
    } finally {
      dispatch(unsetLoading());
    }
  }, [dispatch, t]);

  const handleCancel = () => {
    setMode("VIEW");
    handleGetData(); // Reload original data
  };

  useEffect(() => {
    handleGetData();
  }, [handleGetData]);

  const handleDeleteMultiUploadById = (index_file: number) => {
    setMultiImage((prev) => prev.filter((_, i: number) => i !== index_file));
  };

  const handleChangeMultiUpload = (newFile: any) => {
    const newArray = Array.isArray(newFile) ? newFile : [newFile];
    if (newFile?.length > 0) {
      setMultiImage([...multiUpload, ...newArray]);
    } else {
      setMultiImage([...multiUpload, newFile]);
    }
  };

  return (
    <ContainerLayout
      title={t("จัดการข้อมูลตัวอย่างการเพิ่มโครงการ")}
      hidden_sub_title
      navigateContent={
        <NavigateListCustom
          navigateData={[
            {
              key: "1",
              name: t("ระบบ Edpex"),
              disabled_match: true,
              pathName: routeName.cms.edpexResultDisplay,
            },
            {
              key: "2",
              name: t("จัดการข้อมูลตัวอย่างการเพิ่มโครงการ"),
              pathName: routeName.cms.edpexSampleProjectData,
            },
          ]}
        />
      }
      buttonContent={
        <ContainerButton
          justifyContent="flex-end"
          buttonData={
            mode === "VIEW"
              ? [
                  {
                    buttonProps: {
                      onClick: () => setMode("UPDATE"),
                    },
                    key: "EDIT",
                    label: t("แก้ไข"),
                    seq: 1,
                    type: "EDIT",
                  },
                ]
              : [
                  {
                    buttonProps: {
                      onClick: handleCancel,
                    },
                    key: "CANCEL",
                    label: t("ยกเลิก"),
                    seq: 1,
                    type: "CANCEL",
                  },
                  {
                    buttonProps: {
                      onClick: () => handleSubmit("active"),
                    },
                    key: "SAVE",
                    label: t("บันทึก"),
                    seq: 2,
                    type: "ADD2",
                  },
                  {
                    buttonProps: {
                      onClick: () => handleSubmit("เผยแพร่"), // i18n-ignore (submit mode value)
                    },
                    key: "PUBLISH",
                    label: t("เผยแพร่"),
                    seq: 3,
                    type: "PUBLISH",
                  },
                ]
          }
        />
      }
      content={
        <>
          <div className="w-full flex justify-between">
            <span className="flex-1">
              <StateComponents
                disabledAllField={mode === "VIEW"}
                typeProps={[
                  {
                    seq: 1,
                    type: "TEXT_FIELD",
                    gridProps: {
                      xs: 12,
                      md: 12,
                    },
                    fieldProps: {
                      value: name,
                      placeholder: t("ชื่อ"),
                      onChange: (e) => setName(e.target.value),
                      required: true,
                    },
                  },
                ]}
              />
            </span>
          </div>
          <br />

          <ImageTracking
            disabled={false}
            required
            headingVariant="h6"
            sxHeading={{ color: colors.themeMainColor }}
            heading={t("แนบรูปภาพ")}
            handleDeleteImageApi={() => {}}
            handleDeleteImageSelect={(_, index: number) =>
              handleDeleteMultiUploadById(index)
            }
            onChange={handleChangeMultiUpload}
            tabIndex={0}
            valueAddFile={multiUpload}
            valueImage={[]}
          />

          <br />

          <AccordionUpload
            defaultExpanded
            required
            enableButtonLeft
            titleName={t("ไฟล์แนบ")}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            acceptedFileTypes=".pdf,.doc,.docx,.jpg,.png,.xlsx"
            maxFileSize={5} // 5MB limit
            multiple={true}
          />
        </>
      }
    />
  );
};

export default EdpexSampleProjectData;
